import React from 'react';
import { BreadcrumbGroupProps } from '@amzn/awsui-components-react';
import SessionHelper from '../../utils/session_helper';
import StorageHelper from '../../utils/storage_helper';
import { LEFT_NAV_FAVORITES } from '../../utils/constants';
import TuxComponent from '../../components/tux_component';
import { EventDispatcher } from '../../utils/events/event_dispatcher';
import { EventTypes } from '../../utils/events/event_types';
import SiteModelService from '../../data/site/site_model_service';
import SiteModel from '../../data/site/site_model';
import { SiteModelType } from '../../data/site/site_base_model';
import ModuleListModel from '../../data/site/module_list_module';
import NonModuleListModel from '../../data/site/non_module_list_model';
import PostCARProposalModel from '../../data/site/post_car_proposal_model';
import NonModuleProposalModel from '../../data/site/non_module_proposal_model';
import ModuleDesignGuideService from '../../data/module/module_design_guide_service';
import ModuleDesignGuideModel from '../../data/module/module_design_guide_model';
import {TUX_LOGO} from '../../resources/base_64_images';
import GlobalSearch from './global_search';
import SideMenu, {
    SideMenuTitle,
    SideMenuLink
} from "@amzn/meridian/side-menu";
import Button from "@amzn/meridian/button";
import Icon from "@amzn/meridian/icon";
import heartTokens from "@amzn/meridian-tokens/base/icon/heart";
import heartFilledTokens from "@amzn/meridian-tokens/base/icon/heart-filled";
import Row from "@amzn/meridian/row";
import folderTokens from "@amzn/meridian-tokens/base/icon/folder";
import homeTokens from "@amzn/meridian-tokens/base/icon/home";
import globeTokens from "@amzn/meridian-tokens/base/icon/globe";
import themeTokens from "@amzn/meridian-tokens/base/icon/theme";
import { BreadCrumbRoute } from '../../routing';
import Select, { SelectOption } from "@amzn/meridian/select";
import UserInfoHelper from '../../utils/user_info_helper';
import RequestHelper, { ActionDetails, AnalyticsRoutes } from '../../utils/request_helper';

interface LeftNavProps {}

interface NavLink {
    id: string;
    text: string;
    href?: string;
    type?: 'link' | 'expandable-link-group';
    icon?: string;
    items?: NavLink[];
    defaultExpanded?: boolean;
}


export default class LeftNav extends TuxComponent<LeftNavProps, any> {
    public static RECENTS_KEY = 'ESPLM_LEFT_NAV_RECENTS';
    public static RECENTS_CLEARED_KEY = 'ESPLM_LEFT_NAV_RECENTS_CLEARED';

    favoritesExpanded = false;
    recentsExpanded = false;
    changesExpanded = false;
    recents: NavLink[] = LeftNav.globalRecents;
    favorites: NavLink[] = LeftNav.globalFavorites;
    changes: NavLink[] = LeftNav.globalChanges;
    menuOpen = true;

    constructor(props: LeftNavProps) {
        super(props);
        this.bindAll(this);

        EventDispatcher.subscribe(EventTypes.RoutingChange, LeftNav.RECENTS_KEY, this.addToRecents);
        // This is from constants since it's both the event key and the localStorage key
        EventDispatcher.subscribe(EventTypes.FavoritesChange, LEFT_NAV_FAVORITES, this.addToFavorites);
        EventDispatcher.subscribe(EventTypes.RecentsCleared, LeftNav.RECENTS_CLEARED_KEY, this.clearRecents);
    }

    public async loadData() {
        // throw new Error('Method not implemented.');
    }

    get currentPageName(): string {
        return BreadCrumbRoute.currentRoute?.text ?? '';
    }

    get _favorites(): Set<string> {
        const output = new Set<string>();
        const favs = StorageHelper.getObject<SideNavigationProps.Link[]>(LEFT_NAV_FAVORITES) ?? [];

        for (const fav of favs) {
            output.add(fav.text);
        }

        return output;
    }

    favoritesPressed = () => {
        EventDispatcher.triggerEvent(EventTypes.FavoritesChange, BreadCrumbRoute.currentRoute);
        this.dataUpdated();
    }

    getFavoriteButton() {
        return (
            <Button
                type={'icon'}
                onClick={this.favoritesPressed}
            >
                <Icon tokens={this._favorites.has(this.currentPageName) ? heartFilledTokens : heartTokens}>Favorites</Icon>
            </Button>
        );
    }

    /**
     * Updates the region and reloads the current page
     * @param e
     * @returns
     */
    onRegionChange(newRegion: string) {
        if (process.env.NODE_ENV !== 'production') {
            console.log('(DEBUG)', newRegion);
        }
        const oldRegion = SessionHelper.getRegion();
        if (newRegion === SessionHelper.getRegion()) {
            return;
        }
        RequestHelper.logAnalytics(
            AnalyticsRoutes.action,
            new ActionDetails('region_changed', `${SessionHelper.getRegion()}>${newRegion}`),
        );
        SessionHelper.setRegion(newRegion);
        window.location.hash = window.location.hash.replace(oldRegion, newRegion);
        window.location.reload();
    }

    get defaultNavItems(): NavLink[] {
        return [
            {
                id: 'home',
                type: 'link',
                text: 'Home',
                href: `#/${SessionHelper.getRegion()}/home`,
                icon: homeTokens,

            },
            {
                id: 'sites',
                type: 'link',
                text: 'Sites',
                href: `#/${SessionHelper.getRegion()}/sites`,
                icon: globeTokens,
            },
            {
                id: 'module-design-guides',
                type: 'link',
                text: 'Module Design Guides',
                href: `#/${SessionHelper.getRegion()}/moduleDesignGuides`,
                icon: themeTokens,
            },
            {
                id: 'modules',
                type: 'link',
                text: 'Modules',
                href: `#/${SessionHelper.getRegion()}/modules`,
                icon: folderTokens,
            },
            {
                id: 'recents',
                type: 'expandable-link-group',
                text: 'Recents',
                items: this.recents as NavLink[],
                defaultExpanded: this.recentsExpanded,
            },
            {
                id: 'expand-favorites',
                type: 'expandable-link-group',
                text: (
                    <Row width={'100%'} alignmentHorizontal="justify" spacing={'600'}>
                        <span>Favorites</span>
                        {this.getFavoriteButton()}
                    </Row>
                ),
                items: this.favorites as NavLink[],
                defaultExpanded: this.favoritesExpanded,
            },
            {
                id: 'changes',
                type: 'expandable-link-group',
                text: 'Changes',
                items: this.changes,
                defaultExpanded: this.changesExpanded,
            },
        ];
    }

    get navItems(): NavLink[] {
        const output = this.defaultNavItems;

        if (process.env.NODE_ENV !== 'production') {
            output.push({
                id: 'test',
                text: 'Test (Remove for Go-Live)',
                href: `#/test`,
            });
        }

        return output;
    }

    static get globalFavorites(): NavLink[] {
        return StorageHelper.getObject<NavLink[]>(LEFT_NAV_FAVORITES, []) ?? [];
    }

    static get globalRecents(): NavLink[] {
        return StorageHelper.getObject<NavLink[]>(LeftNav.RECENTS_KEY, []) ?? [];
    }

    static get globalChanges(): NavLink[] {
        const changesLeftNav: NavLink[] = [
            {
                id: 'archive-changes',
                type: 'link',
                text: 'Archive Changes',
                href: `#/${SessionHelper.getRegion()}/changes/archive`,
            },
            {
                id: 'major-changes',
                type: 'link',
                text: 'Major Changes',
                href: `#/${SessionHelper.getRegion()}/changes/major`,
            },
            {
                id: 'minor-changes',
                type: 'link',
                text: 'Minor Changes',
                href: `#/${SessionHelper.getRegion()}/changes/minor`,
            },
        ];

        return changesLeftNav;
    }

    // Function for sites to add Site Name and MDG to add MDG Name to Recent instead of IDs
    async changeRouteIdToText(route: BreadcrumbGroupProps.Item) {
        if (route.href.includes('sites')) {
            let siteIdMatch = route.href.match('sites/([a-zA-Z0-9]*)/relatedItems/([a-zA-Z0-9]*)/([a-zA-Z0-9]*)') ?? [];
            if (siteIdMatch?.length === 0) {
                siteIdMatch = route.href.match('sites/([a-zA-Z0-9]*)/') ?? [];
                if (siteIdMatch?.length < 2) {
                    return route.text;
                }
                const siteId = siteIdMatch[1];
                const site: SiteModel = (await SiteModelService.fetchModel(SiteModelType.Site, siteId)) as SiteModel;
                return site.name ?? '';
            } else {
                if (siteIdMatch?.length < 2) {
                    return route.text;
                }
                const relatedItemType = siteIdMatch[2];
                const relatedItemId = siteIdMatch[3];

                if (relatedItemType === 'ModuleList') {
                    const mdl: ModuleListModel = (await SiteModelService.fetchModel(
                        SiteModelType.ModuleList,
                        relatedItemId,
                    )) as ModuleListModel;
                    return mdl.number ?? '';
                } else if (relatedItemType === 'NonModuleList') {
                    const nml: NonModuleListModel = (await SiteModelService.fetchModel(
                        SiteModelType.NonModuleList,
                        relatedItemId,
                    )) as NonModuleListModel;
                    return nml.number ?? '';
                } else if (relatedItemType === 'PostCARProposal') {
                    const pcp: PostCARProposalModel = (await SiteModelService.fetchModel(
                        SiteModelType.PostCARProposal,
                        relatedItemId,
                    )) as PostCARProposalModel;
                    return pcp.number ?? '';
                } else if (relatedItemType === 'NonModuleProposal') {
                    const nmp: NonModuleProposalModel = (await SiteModelService.fetchModel(
                        SiteModelType.NonModuleProposal,
                        relatedItemId,
                    )) as NonModuleProposalModel;
                    return nmp.number ?? '';
                }
            }
        } else if (route.href.includes('moduleDesignGuide')) {
            /* empty */
            const moduleDesignGuideIdMatch = route.href.match('moduleDesignGuide/([a-zA-Z0-9]*)/') ?? [];
            if (moduleDesignGuideIdMatch?.length < 2) {
                return route.text;
            }
            const moduleDesignGuideId = moduleDesignGuideIdMatch[1];
            const moduleDesignGuideModel: ModuleDesignGuideModel = (await ModuleDesignGuideService.getModuleDesignGuide(
                moduleDesignGuideId,
            )) as ModuleDesignGuideModel;
            return 'MDG - ' + moduleDesignGuideModel.name;
        }

        // Return route.text in default case
        return route.text;
    }

    async addToFavorites(route: BreadcrumbGroupProps.Item) {
        if (!route) {
            this.favorites = LeftNav.globalFavorites;
            this.dataUpdated();
            return;
        }

        if (!route.href || !route.text) {
            return;
        }

        this.favorites = LeftNav.globalFavorites;

        route.text = (await this.changeRouteIdToText(route)) ?? '';

        // Route was already in favorites, remove it
        if (this.favorites.find((f) => f.text === route.text)) {
            this.favorites = this.favorites.filter((f) => f.text !== route.text);
        } else {
            // Insert the new link as the top favorite
            this.favorites = [
                {
                    type: 'link',
                    text: route.text,
                    href: route.href,
                } as NavLink,
            ].concat(this.favorites);
        }

        StorageHelper.setObject(LEFT_NAV_FAVORITES, this.favorites);
        this.dataUpdated();
    }

    async addToRecents(route: BreadcrumbGroupProps.Item) {
        if (!route.href || !route.text) {
            this.dataUpdated();
            return;
        }
        // Don't add links to home to recents
        if (route.href.endsWith('home') || route.href.endsWith('modules')) {
            this.dataUpdated();
            return;
        }

        route.text = (await this.changeRouteIdToText(route)) ?? '';

        this.recents = LeftNav.globalRecents;
        // Filter out the page if it's already present
        this.recents = this.recents.filter((r) => r.href !== route.href && r.text !== route.text);

        // Add the item to recents so it's the latest entry
        this.recents = [
            {
                type: 'link',
                text: route.text,
                href: route.href,
            } as NavLink,
        ].concat(this.recents);

        if (this.recents.length > 10) {
            this.recents = this.recents.slice(0, 10);
        }

        StorageHelper.setObject(LeftNav.RECENTS_KEY, this.recents);
        this.dataUpdated();
    }

    clearRecents() {
        this.recents = [];
        StorageHelper.setObject(LeftNav.RECENTS_KEY, this.recents);
        this.dataUpdated();
    }

    navChanged(e: any) {
        if (e.detail.item.text === 'Recents') {
            this.recentsExpanded = e.detail.expanded;
        } else if (e.detail.item.id === 'expand-favorites') {
            this.favoritesExpanded = e.detail.expanded;
        } else if (e.detail.item.text === 'Changes') {
            this.changesExpanded = e.detail.expanded;
        }
    }

    getRegionDropdown() {
        return (
            <Select
                label={'Region:'}
                size={'xlarge'}
                placeholder={'Select Region'}
                value={SessionHelper.getRegion()}
                onChange={this.onRegionChange}
            >
                {UserInfoHelper.availableRegions.map((option) => (
                    <SelectOption key={option} value={option} label={option} />
                ))}
            </Select>
        );
    }

    onClose = () => {
        this.menuOpen = false;
        this.dataUpdated();
    }

    onOpen = () => {
        this.menuOpen = true;
        this.dataUpdated();
    }


    getHeader() {
        return (
            <div>
                <img
                    height="35px"
                    src={`data:image/png;base64, ${TUX_LOGO}`}
                    alt="TuxLogo"
                    style={{ backgroundColor: 'transparent' }}
                />
                <div className="left-nav-region" style={{ marginTop: '30px'}}>
                    {this.getRegionDropdown()}
                    <div className="left-nav-search" style={{ marginTop: '15px' }}>
                        <GlobalSearch popoverMaxHeight={750}/>
                    </div>
                </div>
            </div>
        );
    }


    render(): JSX.Element {
        const renderNavItem = (item: NavLink, index: number) => {
            if (item.type === 'expandable-link-group') {
                return (
                    <SideMenuLink
                        key={index}
                        data-testid={item.id}
                        open={item.defaultExpanded}
                        onClick={() => {
                            if (item.id === 'expand-favorites') this.favoritesExpanded = !this.favoritesExpanded;
                            if (item.id === 'recents') this.recentsExpanded = !this.recentsExpanded;
                            if (item.id === 'changes') this.changesExpanded = !this.changesExpanded;
                            this.dataUpdated();
                        }}
                    >
                        {item.text}
                        {item.items?.map((subItem, subIndex) => (
                            <SideMenuLink key={subIndex} href={subItem.href}>
                                {subItem.text}
                            </SideMenuLink>
                        ))}
                    </SideMenuLink>
                );
            }

            return (
                <SideMenuLink key={index} href={item.href} icon={item.icon}>
                    {item.text}
                </SideMenuLink>
            );
        };

        return (
            <SideMenu
                data-testid="side-navigation"
                className="leftNav"
                open={this.menuOpen}
                onClose={this.onClose}
                onOpen={this.onOpen}
                type="skinny"
            >
                <SideMenuTitle>
                    {this.getHeader()}
                </SideMenuTitle>
                {this.navItems.map(renderNavItem)}
            </SideMenu>
        );
    }
}
